import {
  BidValidationError,
  roundHalfUpCents,
  type BidInput,
  type BidLine,
  type BidResult,
  type Cents,
  type ColorOption,
  type PackageDef,
  type PackageQuote,
  type PricingConfig,
  type SurfaceDef,
} from '@lbhl/shared';
import { computeMaterials } from './materials.js';
import { resolveAndValidate } from './validate.js';
import { ENGINE_VERSION } from './version.js';

/**
 * Turns one set of measurements into a price for every package.
 *
 * The customer receives all three and chooses afterwards, so the estimator
 * measures once: each tier is priced on the surfaces that tier includes, which
 * the pricing config's package/surface join already defines. Signature's footage
 * therefore contains Essential's, and Showcase's contains both. Nothing here
 * branches on a package name.
 *
 * Pure: no I/O, no clock, no randomness. The same config and input always give
 * the same result, which is what lets the PWA price a bid offline and the server
 * verify that price later from the same source.
 *
 * Rounding contract: every line rounds half-up to a whole cent, and the totals
 * are the sum of those rounded lines. Rounding once at the end would let the
 * printed lines disagree with the printed total by a cent.
 */
export function computeBid(config: PricingConfig, input: BidInput): BidResult {
  const { context, issues } = resolveAndValidate(config, input);
  if (issues.length > 0) throw new BidValidationError(issues);

  const { packagesInOrder, surfacesByCode } = context;
  const colorOption = config.colorOptions.find((c) => c.code === input.colorCode)!;

  // --- Travel, per mile -------------------------------------------------------
  // Computed once: it is the same drive whichever package the customer picks, so
  // every quote carries the identical fee folded into its all-in price.
  //
  // The free radius is a distance from base, so it comes off the one-way
  // distance before any round-trip doubling.
  const chargeableOneWay = Math.max(0, input.travelMiles - config.travelFreeRadiusMiles);
  const billableTravelMiles =
    config.travelBasis === 'round_trip' ? chargeableOneWay * 2 : chargeableOneWay;
  const travelFeeCents = roundHalfUpCents(billableTravelMiles * config.travelRateCentsPerMile);

  // A Set because the cost model's warnings are properties of the config, not of
  // a tier, so all three quotes raise them identically.
  const warnings = new Set<string>();
  const quotes: PackageQuote[] = [];
  const customerLines: BidLine[] = [];

  for (const [tierIndex, pkg] of packagesInOrder.entries()) {
    const quote = quoteForPackage({
      config,
      input,
      pkg,
      surfacesByCode,
      colorOption,
      billableTravelMiles,
      travelFeeCents,
      manualCounts: cumulativeEngineering(input, packagesInOrder, tierIndex),
      warnings,
    });

    quotes.push(quote);

    // The single all-in line this package's customer actually sees.
    customerLines.push({
      sort: tierIndex,
      description: `${pkg.name} Package — ${input.seasonLabel}`,
      qty: 1,
      uom: 'bid',
      unitPriceCents: quote.totalCents,
      amountCents: quote.totalCents,
      isCustomerVisible: true,
    });
  }

  return {
    engineVersion: ENGINE_VERSION,
    pricingVersionId: config.pricingVersionId,
    quotes,
    customerLines,
    warnings: [...warnings],
  };
}

interface QuoteArgs {
  config: PricingConfig;
  input: BidInput;
  pkg: PackageDef;
  surfacesByCode: Map<string, SurfaceDef>;
  colorOption: ColorOption;
  billableTravelMiles: number;
  travelFeeCents: Cents;
  manualCounts: Map<string, number>;
  warnings: Set<string>;
}

function quoteForPackage(args: QuoteArgs): PackageQuote {
  const {
    config,
    input,
    pkg,
    surfacesByCode,
    colorOption,
    billableTravelMiles,
    travelFeeCents,
    manualCounts,
    warnings,
  } = args;

  const ratePerFootCents =
    (pkg.ratePerFootOverrideCents ?? config.ratePerFootCents) +
    colorOption.perFootPriceDeltaCents;

  const lines: BidLine[] = [];
  let sort = 0;

  // This package's surfaces, and only those. The join is the entire mechanism by
  // which a higher tier costs more: same rate, more measured footage.
  const measured = input.measurements
    .map((m) => ({ m, surface: surfacesByCode.get(m.surfaceCode)! }))
    .filter(({ surface }) => pkg.surfaces.includes(surface.code))
    .sort((a, b) => a.surface.sort - b.surface.sort);

  // --- Footage-bearing C9 runs -------------------------------------------------
  let totalLinearFeet = 0;
  let runCount = 0;

  for (const { m, surface } of measured.filter(({ surface }) => !surface.isAccent)) {
    const feet = m.linearFeet ?? 0;
    if (feet <= 0) continue;
    totalLinearFeet += feet;
    runCount += 1;
    lines.push({
      sort: sort++,
      description: `${surface.name} — C9 (${colorOption.name})`,
      qty: feet,
      uom: 'foot',
      unitPriceCents: ratePerFootCents,
      amountCents: roundHalfUpCents(feet * ratePerFootCents),
      isCustomerVisible: false,
    });
  }

  // --- Accent mini-light strands (Showcase) -----------------------------------
  let totalStrands = 0;

  for (const { m, surface } of measured.filter(({ surface }) => surface.isAccent)) {
    const strands = m.strandCount ?? 0;
    if (strands <= 0) continue;
    totalStrands += strands;
    lines.push({
      sort: sort++,
      description: `${surface.name} — accent mini-light strands`,
      qty: strands,
      uom: 'strand',
      unitPriceCents: config.pricePerStrandCents,
      amountCents: roundHalfUpCents(strands * config.pricePerStrandCents),
      isCustomerVisible: false,
    });
  }

  const subtotalCents: Cents = lines.reduce((sum, l) => sum + l.amountCents, 0);

  if (travelFeeCents > 0) {
    lines.push({
      sort: sort++,
      description: `Travel — ${billableTravelMiles} mi (${config.travelBasis === 'round_trip' ? 'round trip' : 'one way'})`,
      qty: billableTravelMiles,
      uom: 'mile',
      unitPriceCents: config.travelRateCentsPerMile,
      amountCents: travelFeeCents,
      isCustomerVisible: false,
    });
  }

  // --- manual adjustments -----------------------------------------------------
  // Applied last, so an adjustment means what an estimator expects: a change to
  // the final number, not something the travel or package maths then acts on.
  // Only this package's adjustments: a discount offered on Signature must not
  // move the Essential price the same customer is looking at.
  let adjustmentTotalCents = 0;

  for (const adjustment of input.adjustments ?? []) {
    if (adjustment.packageCode !== pkg.code) continue;
    if (adjustment.amountCents === 0) continue;
    adjustmentTotalCents += adjustment.amountCents;
    lines.push({
      sort: sort++,
      description: `Adjustment — ${adjustment.label}`,
      qty: 1,
      uom: 'adjustment',
      unitPriceCents: adjustment.amountCents,
      amountCents: adjustment.amountCents,
      isCustomerVisible: false,
    });
  }

  const totalCents: Cents = subtotalCents + travelFeeCents + adjustmentTotalCents;

  // A negative bid is never a real quote; it means a mistyped adjustment. Catch it
  // here rather than letting it reach a customer or a margin report.
  if (totalCents < 0) {
    throw new BidValidationError([
      `Adjustments of ${adjustmentTotalCents} cents would make the ${pkg.name} total negative.`,
    ]);
  }

  // --- Internal material cost and margin --------------------------------------
  const {
    materials,
    estMaterialCostCents,
    warnings: materialWarnings,
  } = computeMaterials(config, input, {
    totalLinearFeet,
    totalStrands,
    runCount,
    hasAccent: totalStrands > 0,
    manualCounts,
  });
  for (const w of materialWarnings) warnings.add(w);

  const materialsMarginCents = totalCents - estMaterialCostCents;
  const materialsMarginPct =
    totalCents === 0 ? 0 : Number(((materialsMarginCents / totalCents) * 100).toFixed(2));

  if (adjustmentTotalCents !== 0 && subtotalCents > 0) {
    const share = Math.abs(adjustmentTotalCents) / subtotalCents;
    if (share >= 0.15) {
      warnings.add(
        `Manual adjustments move the ${pkg.name} bid by ${(share * 100).toFixed(0)}% of its lighting subtotal.`,
      );
    }
  }

  if (pkg.includesAccent && totalStrands === 0) {
    warnings.add(`${pkg.name} includes accent lighting but no strands were entered.`);
  }

  return {
    packageCode: pkg.code,
    ratePerFootCents,
    totalLinearFeet: Number(totalLinearFeet.toFixed(2)),
    totalStrands,
    runCount,
    lines,
    subtotalCents,
    travelFeeCents,
    billableTravelMiles: Number(billableTravelMiles.toFixed(2)),
    adjustmentTotalCents,
    totalCents,
    materials,
    estMaterialCostCents,
    materialsMarginCents,
    materialsMarginPct,
  };
}

/**
 * Engineering counts for one tier, accumulated through every lower tier.
 *
 * Additive like footage: a bidder enters what each tier ADDS, so Showcase's plug
 * count is its own plus Signature's plus Essential's. The chain follows the
 * config's package sort order, which is the same order the form lays the sections
 * out in — nothing here knows a tier by name.
 */
function cumulativeEngineering(
  input: BidInput,
  packagesInOrder: PackageDef[],
  tierIndex: number,
): Map<string, number> {
  const included = new Set(
    packagesInOrder.slice(0, tierIndex + 1).map((p) => p.code),
  );

  const counts = new Map<string, number>();
  for (const entry of input.engineering ?? []) {
    if (!included.has(entry.packageCode)) continue;
    if (entry.qty <= 0) continue;
    counts.set(entry.sku, (counts.get(entry.sku) ?? 0) + entry.qty);
  }
  return counts;
}
